<!--
  Statistiche — Dashboard layout (redesigned 2026-05-25).

  Structure:
    1. Sticky header with title, year selector and PDF export.
    2. KPI tiles row (clickable, scroll to the related chart).
    3. Responsive charts grid (2 columns desktop, 1 column mobile).

  Notes:
    - The year selector previously used [selectedIndex]="index" with
      `index` undefined → Kendo defaulted to -1 then the ngModel
      pushed it to 0 in the same CD pass → NG0100. Binding removed
      here; [(ngModel)]="chosenYear" is enough.
    - Unused canvases (giorniLavorativi, corriere2, errori) were
      removed from the template — none of the API subscribes populate
      them. They can be reintroduced when the backend exposes the
      data.
-->

<div class="stats-page" id="report-content">

  <!-- ─── Sticky header ─── -->
  <header class="stats-header">
    <div class="header-titles">
      <h1 class="stats-title">Statistiche</h1>
      <p class="stats-subtitle">Panoramica delle performance operative · anno {{ chosenYear }}</p>
    </div>

    <div class="header-actions">
      <div class="year-picker">
        <label for="year">Anno</label>
        <kendo-dropdownlist
          id="year"
          [data]="years"
          [value]="chosenYear"
          (valueChange)="changeYear($event)"
          class="year-dropdown"
        >
          <ng-template let-dataItem>{{ dataItem }}</ng-template>
        </kendo-dropdownlist>
      </div>

      <!--
        Month filter (Batch 6). Empty selection ("Tutto l'anno") resets
        to year mode. When a month is selected, KPI tiles, highlights
        and leaderboards "zoom in" on that month. The monthly trend
        charts stay full-year because the timeline IS the chart.
      -->
      <div class="year-picker">
        <label for="month">Mese</label>
        <kendo-dropdownlist
          id="month"
          [data]="MONTHS_FOR_FILTER"
          [value]="chosenMonthName || MONTHS_FOR_FILTER[0]"
          (valueChange)="changeMonth($event)"
          class="year-dropdown"
        >
          <ng-template let-dataItem>{{ dataItem }}</ng-template>
        </kendo-dropdownlist>
      </div>

      <button class="btn btn-export-pdf" (click)="generatePDF()" title="Esporta tutta la pagina come PDF">
        <i class="bx bxs-file-pdf"></i>
        <span>Esporta PDF</span>
      </button>
    </div>
  </header>

  <hr class="stats-divider" />

  <!-- ─── Year summary line ─── -->
  @if (isInitialLoading) {
    <div class="skeleton skeleton-summary"></div>
  } @else {
    <p class="year-summary">{{ yearSummaryLine }}</p>
  }

  <!-- ─── SECTION: Indicatori principali ─── -->
  <h2 class="section-heading">📊 Indicatori principali</h2>
  @if (isInitialLoading) {
    <section class="kpi-row">
      <div class="skeleton skeleton-kpi"></div>
      <div class="skeleton skeleton-kpi"></div>
      <div class="skeleton skeleton-kpi"></div>
      <div class="skeleton skeleton-kpi"></div>
    </section>
  }
  <section class="kpi-row" aria-label="Indicatori principali" [class.hidden-while-loading]="isInitialLoading">

    <article class="kpi-tile kpi-blue" (click)="scrollToElement('ordini', 'highlight1')" title="Vai al grafico ordini evasi">
      <header class="kpi-header">
        <h2 class="kpi-label">{{ isMonthMode ? 'Ordini evasi (' + chosenMonthName + ')' : 'Ordini evasi' }}</h2>
        <i class="bx bx-task kpi-icon"></i>
      </header>
      <div class="kpi-value">
        {{ kpiTotalOrders ?? '—' }}
      </div>
      <div class="kpi-sparkline"><canvas #sparkOrdiniCanvas></canvas></div>
      @if (!isMonthMode && generalStatistics?.s_t_orders_perc !== null && generalStatistics?.s_t_orders_perc !== undefined) {
        <footer class="kpi-trend"
                [class.trend-up]="(generalStatistics?.s_t_orders_perc ?? 0) > 0"
                [class.trend-down]="(generalStatistics?.s_t_orders_perc ?? 0) < 0">
          <i class="bx" [class.bx-trending-up]="(generalStatistics?.s_t_orders_perc ?? 0) >= 0"
                       [class.bx-trending-down]="(generalStatistics?.s_t_orders_perc ?? 0) < 0"></i>
          <span>{{ (generalStatistics?.s_t_orders_perc | number : '1.1-1') }}% vs anno precedente</span>
        </footer>
      }
    </article>

    <article class="kpi-tile kpi-purple" (click)="scrollToElement('media-ordini', 'highlight3')" title="Vai al grafico media ordini">
      <header class="kpi-header">
        <h2 class="kpi-label">{{ isMonthMode ? 'Media ordini (' + chosenMonthName + ')' : 'Media ordini / giorno' }}</h2>
        <i class="bx bx-trending-up kpi-icon"></i>
      </header>
      <div class="kpi-value">
        {{ (kpiAvgOrdersPerDay | number : '1.0-1') ?? '—' }}
      </div>
      <div class="kpi-sparkline"><canvas #sparkMediaOrdiniCanvas></canvas></div>
      <footer class="kpi-meta">
        <span>Tempo medio preparazione: {{ tempoPrepInMinuti }} min</span>
      </footer>
    </article>

    <article class="kpi-tile kpi-yellow" (click)="scrollToElement('colli', 'highlight2')" title="Vai al grafico colli spediti">
      <header class="kpi-header">
        <h2 class="kpi-label">{{ isMonthMode ? 'Colli spediti (' + chosenMonthName + ')' : 'Colli spediti' }}</h2>
        <i class="bx bxs-package kpi-icon"></i>
      </header>
      <div class="kpi-value">
        {{ kpiTotalBoxes ?? '—' }}
      </div>
      <div class="kpi-sparkline"><canvas #sparkColliCanvas></canvas></div>
      <footer class="kpi-meta">
        <span>{{ isMonthMode ? 'Totale del mese' : 'Totale annuo' }}</span>
      </footer>
    </article>

    <article class="kpi-tile kpi-green" (click)="scrollToElement('media-spedizioni', 'highlight4')" title="Vai al grafico media spedizioni">
      <header class="kpi-header">
        <h2 class="kpi-label">{{ isMonthMode ? 'Media spedizioni (' + chosenMonthName + ')' : 'Media spedizioni / giorno' }}</h2>
        <i class="bx bxs-truck kpi-icon"></i>
      </header>
      <div class="kpi-value">
        {{ (kpiAvgShipmentsPerDay | number : '1.0-1') ?? '—' }}
      </div>
      <div class="kpi-sparkline"><canvas #sparkMediaSpedCanvas></canvas></div>
      <footer class="kpi-meta">
        <span>{{ isMonthMode ? 'Media del mese' : 'Media annuale' }}</span>
      </footer>
    </article>

  </section>

  <!-- ─── SECTION: Highlights ─── -->
  <h2 class="section-heading">✨ {{ isMonthMode ? 'Highlights di ' + chosenMonthName : 'Highlights dell\'anno' }}</h2>
  <section class="highlights-row" aria-label="Highlights">

    <article class="highlight-card">
      <div class="highlight-icon icon-blue"><i class="bx bxs-calendar-star"></i></div>
      <div class="highlight-body">
        <span class="highlight-label">{{ isMonthMode ? 'Mese selezionato' : 'Mese migliore' }}</span>
        @if (isMonthMode) {
          <span class="highlight-value">{{ chosenMonthName }}</span>
          <span class="highlight-meta">{{ kpiTotalOrders ?? 0 }} ordini evasi</span>
        } @else if (bestMonth) {
          <span class="highlight-value">{{ bestMonth.name }}</span>
          <span class="highlight-meta">{{ bestMonth.count }} ordini evasi</span>
        } @else {
          <span class="highlight-value highlight-empty">—</span>
          <span class="highlight-meta">Nessun dato</span>
        }
      </div>
    </article>

    <article class="highlight-card">
      <div class="highlight-icon icon-purple"><i class="bx bxs-user-check"></i></div>
      <div class="highlight-body">
        <span class="highlight-label">Operatore top</span>
        @if (topOperator) {
          <span class="highlight-value">{{ topOperator.name }}</span>
          <span class="highlight-meta">{{ topOperator.count }} ordini</span>
        } @else {
          <span class="highlight-value highlight-empty">—</span>
          <span class="highlight-meta">Nessun dato</span>
        }
      </div>
    </article>

    <article class="highlight-card">
      <div class="highlight-icon icon-yellow"><i class="bx bxs-truck"></i></div>
      <div class="highlight-body">
        <span class="highlight-label">Corriere principale</span>
        @if (topCarrier) {
          <span class="highlight-value">{{ topCarrier.name }}</span>
          <span class="highlight-meta">{{ topCarrier.count }} consegne</span>
        } @else {
          <span class="highlight-value highlight-empty">—</span>
          <span class="highlight-meta">Nessun dato</span>
        }
      </div>
    </article>

    <article class="highlight-card">
      <div class="highlight-icon icon-green"><i class="bx bxs-package"></i></div>
      <div class="highlight-body">
        <span class="highlight-label">Media colli / ordine</span>
        <span class="highlight-value">{{ avgBoxesPerOrder | number : '1.1-2' }}</span>
        <span class="highlight-meta">Colli per ordine evaso</span>
      </div>
    </article>

  </section>

  <!-- ─── SECTION: Andamenti mensili ─── -->
  <h2 class="section-heading">📈 Andamenti mensili</h2>
  <section class="charts-grid" aria-label="Grafici di dettaglio">

    <article id="ordini" class="chart-card">
      <header class="chart-card-header">
        <h3 class="chart-card-title">Ordini evasi per mese</h3>
        <button class="btn-icon-excel"
                title="Esporta dati in Excel"
                (click)="exportChartDataToExcel(ordiniEvasiChart, charts.ordini_evasi.labels, 'Ordini_evasi', chosenYear)">
          <i class="fas fa-file-excel"></i>
        </button>
      </header>
      <div class="chart-card-body">
        <canvas #ordiniEvasiCanvas></canvas>
        @if (isInitialLoading) {
          <div class="chart-skeleton"></div>
        } @else if (!hasOrdiniEvasiData) {
          <div class="chart-empty-state">
            <i class="bx bx-bar-chart-alt-2"></i>
            <p>Nessun dato disponibile per {{ chosenYear }}</p>
          </div>
        }
      </div>
    </article>

    <article id="media-ordini" class="chart-card">
      <header class="chart-card-header">
        <h3 class="chart-card-title">Media giornaliera ordini</h3>
        <button class="btn-icon-excel"
                title="Esporta dati in Excel"
                (click)="exportChartDataToExcel(mediaOrdiniChart, charts.media_ordini.labels, 'Media_giornaliera_ordini', chosenYear)">
          <i class="fas fa-file-excel"></i>
        </button>
      </header>
      <div class="chart-card-body">
        <canvas #mediaOrdiniCanvas></canvas>
        @if (isInitialLoading) {
          <div class="chart-skeleton"></div>
        } @else if (!hasMediaOrdiniData) {
          <div class="chart-empty-state">
            <i class="bx bx-bar-chart-alt-2"></i>
            <p>Nessun dato disponibile per {{ chosenYear }}</p>
          </div>
        }
      </div>
    </article>

    <article id="colli" class="chart-card">
      <header class="chart-card-header">
        <h3 class="chart-card-title">Colli spediti per mese</h3>
        <button class="btn-icon-excel"
                title="Esporta dati in Excel"
                (click)="exportChartDataToExcel(colliSpeditiChart, charts.colli_spediti.labels, 'Colli_spediti', chosenYear)">
          <i class="fas fa-file-excel"></i>
        </button>
      </header>
      <div class="chart-card-body">
        <canvas #colliSpeditiCanvas></canvas>
        @if (isInitialLoading) {
          <div class="chart-skeleton"></div>
        } @else if (!hasColliSpeditiData) {
          <div class="chart-empty-state">
            <i class="bx bxs-package"></i>
            <p>Nessun dato disponibile per {{ chosenYear }}</p>
          </div>
        }
      </div>
    </article>

    <article id="media-spedizioni" class="chart-card">
      <header class="chart-card-header">
        <h3 class="chart-card-title">Media giornaliera spedizioni</h3>
        <button class="btn-icon-excel"
                title="Esporta dati in Excel"
                (click)="exportChartDataToExcel(mediaSpedizioniChart, charts.media_spedizioni.labels, 'Media_giornaliera_spedizioni', chosenYear)">
          <i class="fas fa-file-excel"></i>
        </button>
      </header>
      <div class="chart-card-body">
        <canvas #mediaSpedizioniCanvas></canvas>
        @if (isInitialLoading) {
          <div class="chart-skeleton"></div>
        } @else if (!hasMediaSpedizioniData) {
          <div class="chart-empty-state">
            <i class="bx bxs-truck"></i>
            <p>Nessun dato disponibile per {{ chosenYear }}</p>
          </div>
        }
      </div>
    </article>

    <article class="chart-card">
      <header class="chart-card-header">
        <h3 class="chart-card-title">Ordini totali per operatore</h3>
        <button class="btn-icon-excel"
                title="Esporta dati in Excel"
                (click)="exportChartDataToExcel(totaleOperatoriChart, charts.totale_operatori.labels, 'Ordini_per_Operatore', chosenYear, 'Operatore')">
          <i class="fas fa-file-excel"></i>
        </button>
      </header>
      <div class="chart-card-body">
        <canvas #totaleOperatoriCanvas></canvas>
        @if (isInitialLoading) {
          <div class="chart-skeleton"></div>
        } @else if (!hasOperatoriData) {
          <div class="chart-empty-state">
            <i class="bx bxs-user-circle"></i>
            <p>Nessun dato disponibile per {{ chosenYear }}</p>
          </div>
        }
      </div>
    </article>

    <article class="chart-card">
      <header class="chart-card-header">
        <h3 class="chart-card-title">Ordini per operatore (per mese)</h3>
        <button class="btn-icon-excel"
                title="Esporta dati in Excel"
                (click)="exportChartDataToExcel(operatore, operatoreLabels, 'Ordini_per_Operatore_per_mese', chosenYear)">
          <i class="fas fa-file-excel"></i>
        </button>
      </header>
      <div class="chart-card-body">
        <canvas #operatoreCanvas></canvas>
        @if (isInitialLoading) {
          <div class="chart-skeleton"></div>
        } @else if (!hasOperatoreMensileData) {
          <div class="chart-empty-state">
            <i class="bx bx-bar-chart-alt-2"></i>
            <p>Nessun dato disponibile per {{ chosenYear }}</p>
          </div>
        }
      </div>
    </article>

    <article class="chart-card">
      <header class="chart-card-header">
        <h3 class="chart-card-title">Colli spediti per corriere</h3>
        <button class="btn-icon-excel"
                title="Esporta dati in Excel"
                (click)="exportChartDataToExcel(totaleCorrieriChart, charts.totale_corrieri.labels, 'Colli_per_Corriere', chosenYear, 'Corriere')">
          <i class="fas fa-file-excel"></i>
        </button>
      </header>
      <div class="chart-card-body">
        <canvas #totaleCorrieriCanvas></canvas>
        @if (isInitialLoading) {
          <div class="chart-skeleton"></div>
        } @else if (!hasCorrieriData) {
          <div class="chart-empty-state">
            <i class="bx bxs-truck"></i>
            <p>Nessun dato disponibile per {{ chosenYear }}</p>
          </div>
        }
      </div>
    </article>

    <article class="chart-card">
      <header class="chart-card-header">
        <h3 class="chart-card-title">Ordini per corriere (per mese)</h3>
        <button class="btn-icon-excel"
                title="Esporta dati in Excel"
                (click)="exportChartDataToExcel(corriere, charts.ordini_corriere.labels, 'Ordini_per_Corriere_per_mese', chosenYear)">
          <i class="fas fa-file-excel"></i>
        </button>
      </header>
      <div class="chart-card-body">
        <canvas #corriereCanvas></canvas>
        @if (isInitialLoading) {
          <div class="chart-skeleton"></div>
        } @else if (!hasCorriereMensileData) {
          <div class="chart-empty-state">
            <i class="bx bx-bar-chart-alt-2"></i>
            <p>Nessun dato disponibile per {{ chosenYear }}</p>
          </div>
        }
      </div>
    </article>

  </section>

  <!-- ─── SECTION: Classifiche ─── -->
  <h2 class="section-heading">🏆 Classifiche</h2>
  <section class="leaderboards-row" aria-label="Classifiche">

    <article class="leaderboard-card">
      <header class="leaderboard-header">
        <h3 class="leaderboard-title">
          <i class="bx bxs-user-circle"></i>
          Top 5 operatori
        </h3>
        <span class="leaderboard-subtitle">Per numero di ordini evasi</span>
      </header>

      @if (topOperatorsLeaderboard.length > 0) {
        <ol class="leaderboard-list">
          @for (op of topOperatorsLeaderboard; track op.name; let i = $index) {
            <li class="leaderboard-row">
              <span class="leaderboard-rank rank-{{ i + 1 }}">{{ i + 1 }}</span>
              <div class="leaderboard-content">
                <div class="leaderboard-name-row">
                  <span class="leaderboard-name">{{ op.name }}</span>
                  <span class="leaderboard-count">{{ op.count }}</span>
                </div>
                <div class="leaderboard-bar">
                  <div class="leaderboard-bar-fill bar-purple" [style.width.%]="op.percent"></div>
                </div>
              </div>
            </li>
          }
        </ol>
      } @else {
        <p class="leaderboard-empty">Nessun dato disponibile per {{ chosenYear }}.</p>
      }
    </article>

    <article class="leaderboard-card">
      <header class="leaderboard-header">
        <h3 class="leaderboard-title">
          <i class="bx bxs-truck"></i>
          Top 5 corrieri
        </h3>
        <span class="leaderboard-subtitle">Per consegne effettuate</span>
      </header>

      @if (topCarriersLeaderboard.length > 0) {
        <ol class="leaderboard-list">
          @for (c of topCarriersLeaderboard; track c.name; let i = $index) {
            <li class="leaderboard-row">
              <span class="leaderboard-rank rank-{{ i + 1 }}">{{ i + 1 }}</span>
              <div class="leaderboard-content">
                <div class="leaderboard-name-row">
                  <span class="leaderboard-name">{{ c.name }}</span>
                  <span class="leaderboard-count">{{ c.count }}</span>
                </div>
                <div class="leaderboard-bar">
                  <div class="leaderboard-bar-fill bar-blue" [style.width.%]="c.percent"></div>
                </div>
              </div>
            </li>
          }
        </ol>
      } @else {
        <p class="leaderboard-empty">Nessun dato disponibile per {{ chosenYear }}.</p>
      }
    </article>

  </section>
</div>
